import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import api from './api';
import { QRCodeSVG } from 'qrcode.react';
import iconSettings from './assets/icon_settings.png';
import iconKeys from './assets/icon_keys.png';
import iconLeave from './assets/icon_leave.png';

function UserBadge() {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedMemo, setCopiedMemo] = useState(false);
  const [activeView, setActiveView] = useState('menu'); 
  
  const [userData, setUserData] = useState({ 
    username: 'Пользователь', 
    email: '',
    rating: 5.0,
    balance: 0.00,       
    rub_balance: 0.00,   
    completed_tasks: 0   
  });
  
  // 🆕 Состояния для истории заказов
  const [completedTasks, setCompletedTasks] = useState([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);

  const [isPaying, setIsPaying] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('10'); 
  const [cryptoBotAmount, setCryptoBotAmount] = useState('500'); 
  const [cryptoBotMode, setCryptoBotMode] = useState('tg'); 
  const [cryptoBotInvoice, setCryptoBotInvoice] = useState(null); 
  
  const menuRef = useRef(null);
  const [depositData, setDepositData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(3600);
  const [apiKey, setApiKey] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isResetSending, setIsResetSending] = useState(false);
  const [resetParams, setResetParams] = useState({ uid: '', token: '' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isConfirmingPassword, setIsConfirmingPassword] = useState(false);

  const loadProfileData = () => {
    api.get('users/me/') 
      .then(res => setUserData(res.data))
      .catch(err => console.error("Не удалось загрузить данные профиля"));
  };

  useEffect(() => {
    loadProfileData();
    const urlParams = new URLSearchParams(window.location.search);
    const uid = urlParams.get('uid');
    const token = urlParams.get('token');
    if (uid && token) {
      setResetParams({ uid, token });
      setActiveView('reset_password_confirm');
      setIsOpen(true);
    }
    window.addEventListener('balanceUpdated', loadProfileData);
    return () => window.removeEventListener('balanceUpdated', loadProfileData);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
        setTimeout(() => {
          setActiveView('menu');
          setDepositData(null);
          setCryptoBotInvoice(null);
        }, 200);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeView !== 'deposit_info' || !depositData?.expires_at) return;
    const calculateTimeLeft = () => {
      const diffInSeconds = Math.floor((new Date(depositData.expires_at) - new Date()) / 1000);
      return diffInSeconds > 0 ? diffInSeconds : 0;
    };
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [activeView, depositData]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const generateNewKey = () => {
    const randomKey = 'rt_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    setApiKey(randomKey);
  };

  const handleTopUpClick = () => setActiveView('select_method');

  // 🆕 Функция загрузки выполненных заказов
  const fetchCompletedTasks = async () => {
    setIsLoadingTasks(true);
    try {
      // ВАЖНО: Убедись, что на бэкенде есть этот эндпоинт (см. примечание ниже)
      const res = await api.get('tasks/my-completed/'); 
      setCompletedTasks(res.data.slice(0, 10)); // Берем последние 10 задач для компактности
    } catch (err) {
      console.error("Не удалось загрузить историю заказов", err);
      setCompletedTasks([]);
    } finally {
      setIsLoadingTasks(false);
    }
    setActiveView('completed_tasks_list');
  };

  const executeCryptoBotDeposit = async () => {
    const parsedAmount = parseFloat(cryptoBotAmount.toString().replace(',', '.'));
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      alert("Пожалуйста, введите корректную сумму в рублях.");
      return;
    }
    setIsPaying(true);
    setCryptoBotInvoice(null);
    try {
      const response = await api.post('payments/create-invoice/', { amount_rub: parsedAmount });
      if (response.data && response.data.pay_url) {
        if (cryptoBotMode === 'tg') {
          window.location.href = response.data.pay_url;
        } else {
          setCryptoBotInvoice(response.data);
        }
      } else {
        alert("Ошибка генерации счета: " + (response.data.error || "Неизвестная ошибка"));
      }
    } catch (err) {
      console.error("Ошибка при создании счета Crypto Bot:", err);
      alert(err.response?.data?.error || "Не удалось связаться с сервером.");
    } finally {
      setIsPaying(false);
    }
  };

  const executeDeposit = async (networkType) => {
    const parsedAmount = parseFloat(topUpAmount.toString().replace(',', '.'));
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      alert("Пожалуйста, введите корректную сумму.");
      return;
    }
    setIsPaying(true);
    try {
      const response = await api.post('payments/deposit/', { amount: parsedAmount, network: networkType });
      if (response.data && response.data.address) {
        setDepositData(response.data);
        setActiveView('deposit_info');
      } else {
        alert("Ошибка генерации адреса: " + (response.data.error || "Неизвестная ошибка"));
      }
    } catch (err) {
      console.error("Ошибка при создании депозита:", err);
      alert(err.response?.data?.error || "Не удалось связаться с сервером.");
    } finally {
      setIsPaying(false);
    }
  };

  const handleRequestPasswordReset = async (emailToReset) => {
    const email = emailToReset || userData.email || prompt("Введите ваш Email для сброса пароля:");
    if (!email) return;
    setIsResetSending(true);
    try {
      await api.post('request-password-reset/', { email: email.trim() });
      alert('Инструкция по сбросу пароля отправлена на ваш Email!');
      setShowPasswordForm(false);
    } catch (err) {
      alert("Ошибка: " + (err.response?.data?.email?.[0] || err.response?.data?.detail || "Пользователь не найден."));
    } finally {
      setIsResetSending(false);
    }
  };

  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { alert("Пароли не совпадают!"); return; }
    if (newPassword.length < 8) { alert("Пароль должен быть не менее 8 символов."); return; }
    setIsConfirmingPassword(true);
    try {
      await api.post('password-reset-confirm/', { uid: resetParams.uid, token: resetParams.token, password: newPassword });
      alert('Пароль успешно изменен!');
      setNewPassword(''); setConfirmPassword('');
      window.history.replaceState({}, document.title, window.location.pathname);
      setActiveView('menu');
    } catch (err) {
      alert("Ошибка смены пароля. Ссылка могла устареть.");
    } finally {
      setIsConfirmingPassword(false);
    }
  };

  const firstLetter = userData.username ? userData.username.charAt(0).toUpperCase() : 'U';

  const getDynamicTitle = () => {
    if (!isOpen) return null;
    switch (activeView) {
      case 'select_method': return 'Способ пополнения — RealWork.pro';
      case 'cryptobot_amount': return 'Пополнение через Crypto Bot — RealWork.pro';
      case 'select_network': return 'Выбор сети депозита — RealWork.pro';
      case 'deposit_info': return 'Оплата инвойса USDT — RealWork.pro';
      case 'settings': return 'Настройки профиля — RealWork.pro';
      case 'security': return 'Безопасность и API-ключи — RealWork.pro';
      case 'reset_password_confirm': return 'Смена пароля — RealWork.pro';
      case 'completed_tasks_list': return 'История заказов — RealWork.pro'; // 🆕
      default: return `Профиль (${userData.username}) — RealWork.pro`;
    }
  };

  return (
    <div className="relative inline-block text-left font-sans" ref={menuRef}>
      {isOpen && <Helmet><title>{getDynamicTitle()}</title></Helmet>}

      <button 
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setActiveView('menu');
            setDepositData(null);
            setCryptoBotInvoice(null);
          }
        }}
        className="flex items-center gap-3.5 bg-slate-900/60 backdrop-blur-2xl text-white pl-3 pr-5 py-2.5 rounded-full border border-white/15 shadow-[0_10px_30px_rgba(0,0,0,0.4)] hover:border-white/30 hover:scale-[1.02] transition-all duration-300 active:scale-[0.98] group cursor-pointer focus:outline-none"
      >
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center font-black text-sm text-white tracking-wider shadow-inner border border-white/20 group-hover:scale-105 transition-transform duration-300">
            {firstLetter}
          </div>
          <span className="absolute bottom-0 right-0 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] border border-slate-900"></span>
          </span>
        </div>
        <div className="text-left">
          <p className="font-bold text-sm text-slate-100 group-hover:text-white transition-colors tracking-tight leading-tight mb-0.5">{userData.username}</p>
          <div className="flex flex-col text-left">
            <span className="text-[11px] text-emerald-400 font-black tracking-wide leading-none drop-shadow-[0_0_6px_rgba(52,211,153,0.3)]">{Number(userData.balance).toFixed(2)} USDT</span>
            <span className="text-[9px] text-slate-400 font-medium leading-none mt-0.5">≈ {Number(userData.rub_balance).toFixed(2)} ₽</span>
          </div>
        </div>
        <svg className={`w-3.5 h-3.5 text-slate-400 group-hover:text-white transition-transform duration-300 ml-1.5 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-3 w-80 bg-slate-900/85 backdrop-blur-2xl border border-white/15 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] p-5 z-50 menu-dropdown-anim origin-top-left">
         
          {activeView === 'menu' && (
            <div className="animate-in fade-in duration-200">
              <div className="bg-slate-800/50 backdrop-blur-md border border-white/10 rounded-2xl p-4 text-white relative overflow-hidden mb-4 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]">
                <div className="absolute -right-6 -bottom-6 w-20 h-20 bg-indigo-500/20 rounded-full blur-xl pointer-events-none" />
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest mb-0.5">Текущий баланс</p>
                <div className="flex flex-col">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-black tracking-tight">{Number(userData.balance).toFixed(2)}</span>
                    <span className="text-xs font-bold text-indigo-400">USDT</span>
                  </div>
                  <span className="text-xs text-slate-400 font-medium mt-0.5">≈ {Number(userData.rub_balance).toFixed(2)} ₽</span>
                </div>
                <button onClick={handleTopUpClick} disabled={isPaying} className={`mt-4 w-full py-2.5 bg-indigo-600/80 hover:bg-indigo-500/90 active:scale-[0.98] transition-all rounded-xl text-xs font-bold text-center flex items-center justify-center gap-1.5 backdrop-blur-md border border-indigo-400/30 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(0,0,0,0.3)] ${isPaying ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                  💳 Пополнить баланс
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2.5 mb-4">
                <div className="bg-slate-800/40 border border-white/5 p-3 rounded-2xl text-center">
                  <span className="block text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Рейтинг</span>
                  <span className="text-sm font-black text-slate-200 flex items-center justify-center gap-1">⭐ {Number(userData.rating).toFixed(1)}</span>
                </div>
                
                {/* 🆕 СДЕЛАЛИ КЛИКАБЕЛЬНЫМ */}
                <button 
                  onClick={fetchCompletedTasks}
                  className="w-full bg-slate-800/40 border border-white/5 p-3 rounded-2xl text-center hover:bg-white/10 transition cursor-pointer group"
                >
                  <span className="block text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5 group-hover:text-slate-300">Заказы</span>
                  <span className="text-sm font-black text-slate-200 flex items-center justify-center gap-1">
                    🚀 {userData.completed_tasks} шт <span className="text-[9px] text-indigo-400 font-normal">→</span>
                  </span>
                </button>
              </div>

              <div className="border-t border-white/10 my-3" />

              <div className="space-y-1">
                <button onClick={() => setActiveView('settings')} className="w-full text-left px-3.5 py-2.5 hover:bg-white/10 rounded-xl text-xs font-semibold text-slate-200 transition flex items-center gap-3 cursor-pointer">
                  <img src={iconSettings} alt="Настройки" className="w-7 h-7 object-contain shrink-0 drop-shadow-md" />
                  <span>Настройки аккаунта</span>
                </button>
                <button onClick={() => setActiveView('security')} className="w-full text-left px-3.5 py-2.5 hover:bg-white/10 rounded-xl text-xs font-semibold text-slate-200 transition flex items-center gap-3 cursor-pointer">
                  <img src={iconKeys} alt="Безопасность" className="w-7 h-7 object-contain shrink-0 drop-shadow-md" />
                  <span>Безопасность и ключи</span>
                </button>
                <div className="border-t border-white/10 my-2" />
                <button onClick={() => { localStorage.removeItem('token'); window.location.reload(); }} className="w-full text-left px-3.5 py-2.5 hover:bg-rose-500/10 rounded-xl text-xs font-bold text-rose-400 transition flex items-center gap-3 cursor-pointer">
                  <img src={iconLeave} alt="Выход" className="w-7 h-7 object-contain shrink-0 drop-shadow-md" />
                  <span>Выйти из системы</span>
                </button>
              </div>
            </div>
          )}

          {/* 🆕 НОВЫЙ ЭКРАН: ИСТОРИЯ ЗАКАЗОВ */}
          {activeView === 'completed_tasks_list' && (
            <div className="animate-in fade-in duration-200 text-left">
              <button onClick={() => setActiveView('menu')} className="text-xs font-bold text-slate-400 hover:text-white mb-3 flex items-center gap-1 transition cursor-pointer">
                ← Назад в меню
              </button>
              <h3 className="text-sm font-black text-white mb-3">📜 История выполненных</h3>

              {isLoadingTasks ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-[10px] text-slate-400">Загрузка истории...</p>
                </div>
              ) : completedTasks.length === 0 ? (
                <div className="text-center py-6 bg-slate-800/30 rounded-2xl border border-white/5">
                  <p className="text-2xl mb-2">📭</p>
                  <p className="text-xs text-slate-400 font-medium">Вы пока не выполнили ни одного заказа</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto custom-scroll pr-1">
                  {completedTasks.map((task) => (
                    <div key={task.id} className="bg-slate-800/40 border border-white/5 p-3 rounded-xl flex justify-between items-center hover:bg-white/5 transition">
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="text-xs font-bold text-slate-200 truncate">{task.title}</p>
                        <p className="text-[9px] text-slate-500 mt-0.5">
                          {new Date(task.updated_at || task.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="block text-xs font-black text-emerald-400">+{task.price} USDT</span>
                        <span className="text-[9px] text-slate-500">Исполнено</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Остальные экраны (select_method, cryptobot_amount и т.д.) остаются без изменений */}
          {activeView === 'select_method' && (
            <div className="animate-in fade-in duration-200 text-left">
              <button onClick={() => setActiveView('menu')} className="text-xs font-bold text-slate-400 hover:text-white mb-3 flex items-center gap-1 transition cursor-pointer">← Назад в меню</button>
              <h3 className="text-sm font-black text-white mb-1">💳 Выберите способ пополнения</h3>
              <p className="text-[10px] text-slate-400 mb-3.5">Выберите удобный метод оплаты для зачисления баланса.</p>
              <div className="space-y-2.5">
                <button onClick={() => setActiveView('cryptobot_amount')} className="w-full p-3.5 bg-gradient-to-r from-sky-950/40 to-slate-800/40 border border-sky-500/30 hover:border-sky-400/60 hover:bg-white/5 text-left rounded-2xl transition cursor-pointer group">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-bold text-xs text-white group-hover:text-sky-300 transition">Crypto Bot (USDT через Telegram)</span>
                    <span className="text-[9px] font-bold bg-sky-500/20 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded-full">Удобно</span>
                  </div>
                  <span className="block text-[9px] text-slate-400 leading-relaxed">Оплата через Telegram-бота или сканирование QR-кода.</span>
                </button>
                <button onClick={() => setActiveView('select_network')} className="w-full p-3.5 bg-slate-800/40 border border-white/10 hover:border-indigo-400/50 hover:bg-white/5 text-left rounded-2xl transition cursor-pointer group">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-bold text-xs text-slate-200 group-hover:text-indigo-300 transition">Прямой перевод USDT</span>
                    <span className="text-[9px] font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded-full">Web3</span>
                  </div>
                  <span className="block text-[9px] text-slate-400 leading-relaxed">Перевод напрямую на кошелек в сетях TRC-20 или TON.</span>
                </button>
              </div>
            </div>
          )}

          {activeView === 'cryptobot_amount' && (
            <div className="animate-in fade-in duration-200 text-left">
              <button onClick={() => { setActiveView('select_method'); setCryptoBotInvoice(null); }} className="text-xs font-bold text-slate-400 hover:text-white mb-3 flex items-center gap-1 transition cursor-pointer">← Назад к выбору</button>
              <h3 className="text-sm font-black text-white mb-0.5">🤖 Пополнение через Crypto Bot</h3>
              <p className="text-[10px] text-slate-400 mb-3.5">Выберите способ и укажите сумму пополнения.</p>
              {!cryptoBotInvoice ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Вариант оплаты</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setCryptoBotMode('tg')} className={`p-2.5 rounded-xl border text-[11px] font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${cryptoBotMode === 'tg' ? 'border-sky-400 bg-sky-500/20 text-white' : 'border-white/10 bg-slate-950/40 text-slate-400 hover:bg-white/5'}`}>✈️ В Telegram</button>
                      <button type="button" onClick={() => setCryptoBotMode('qr')} className={`p-2.5 rounded-xl border text-[11px] font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${cryptoBotMode === 'qr' ? 'border-sky-400 bg-sky-500/20 text-white' : 'border-white/10 bg-slate-950/40 text-slate-400 hover:bg-white/5'}`}>📱 QR-код</button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Сумма в ₽</label>
                    <input type="number" min="50" step="10" value={cryptoBotAmount} onChange={(e) => setCryptoBotAmount(e.target.value)} placeholder="Укажите сумму, например 500" className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-2.5 text-xs text-white font-bold placeholder-slate-500 focus:outline-none focus:border-sky-500/60" />
                  </div>
                  <button onClick={executeCryptoBotDeposit} disabled={isPaying} className="w-full py-2.5 bg-sky-600/90 hover:bg-sky-500 border border-sky-400/40 text-white text-xs font-bold rounded-xl transition disabled:opacity-50 cursor-pointer shadow-[0_4px_12px_rgba(14,165,233,0.2)]">
                    {isPaying ? '⏳ Генерация счета...' : cryptoBotMode === 'tg' ? 'Оплатить в Telegram →' : 'Показать QR-код →'}
                  </button>
                </div>
              ) : (
                <div className="space-y-3 text-center">
                  <div className="flex justify-center bg-white p-3 rounded-2xl shadow-inner border border-white/20">
                    <QRCodeSVG value={cryptoBotInvoice.pay_url} size={150} bgColor={"#ffffff"} fgColor={"#0f172a"} level={"H"} />
                  </div>
                  <p className="text-[10px] text-slate-300">Отсканируйте QR-код для оплаты счета в Telegram</p>
                  <div className="flex gap-2">
                    <a href={cryptoBotInvoice.pay_url} target="_blank" rel="noopener noreferrer" className="flex-1 py-2 bg-sky-600 hover:bg-sky-500 text-white text-[11px] font-bold rounded-xl transition block text-center">Открыть в TG</a>
                    <button onClick={() => setCryptoBotInvoice(null)} className="py-2 px-3 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-bold rounded-xl transition cursor-pointer">Сброс</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeView === 'select_network' && (
            <div className="animate-in fade-in duration-200 text-left">
              <button onClick={() => setActiveView('select_method')} className="text-xs font-bold text-slate-400 hover:text-white mb-3 flex items-center gap-1 transition cursor-pointer">← Назад к выбору</button>
              <h3 className="text-sm font-black text-white mb-0.5">🌐 Выберите сеть USDT</h3>
              <div className="mb-3">
                <label className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Сумма в USDT</label>
                <input type="number" min="1" step="1" value={topUpAmount} onChange={(e) => setTopUpAmount(e.target.value)} placeholder="Сумма в USDT" className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-2 text-xs text-white font-bold placeholder-slate-500 focus:outline-none focus:border-indigo-500/60" />
              </div>
              <div className="space-y-2">
                <button onClick={() => executeDeposit('trc20')} disabled={isPaying} className="w-full p-3.5 bg-slate-800/40 border border-white/10 hover:border-indigo-400/50 hover:bg-white/5 text-left rounded-2xl transition cursor-pointer">
                  <span className="block font-bold text-xs text-slate-200">TRON (TRC-20)</span>
                  <span className="block text-[9px] text-slate-400 mt-0.5 leading-relaxed">Уникальный адрес для каждого платежа. Быстрое зачисление.</span>
                </button>
                <button onClick={() => executeDeposit('ton')} disabled={isPaying} className="w-full p-3.5 bg-slate-800/40 border border-white/10 hover:border-indigo-400/50 hover:bg-white/5 text-left rounded-2xl transition cursor-pointer">
                  <span className="block font-bold text-xs text-slate-200">The Open Network (TON)</span>
                  <span className="block text-[9px] text-slate-400 mt-0.5 leading-relaxed">Низкие комиссии сети. Требуется обязательный комментарий (Memo).</span>
                </button>
              </div>
            </div>
          )}

          {activeView === 'deposit_info' && depositData && (
            <div className="animate-in fade-in duration-200 text-left">
              <button onClick={() => { setActiveView('menu'); setDepositData(null); }} className="text-xs font-bold text-slate-400 hover:text-white mb-3 flex items-center gap-1 transition cursor-pointer">← Назад в меню</button>
              <h3 className="text-sm font-black text-white mb-0.5">💎 Оплата инвойса</h3>
              <p className="text-[10px] text-slate-400 mb-3">Отсканируйте код или отправьте точную сумму.</p>
              <div className="p-2.5 bg-slate-800/40 border border-white/10 rounded-2xl text-center mb-3">
                <span className="block text-[9px] uppercase font-bold text-slate-400 mb-0.5">Срок действия инвойса</span>
                {timeLeft > 0 ? (
                  <span className="text-xs font-black text-rose-400 font-mono">⏳ {formatTime(timeLeft)}</span>
                ) : (
                  <span className="text-[10px] font-bold text-rose-400 block">⚠️ Срок действия истек.</span>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex justify-center bg-white p-3 rounded-2xl shadow-inner border border-white/20">
                  <QRCodeSVG value={depositData.memo ? `ton://transfer/${depositData.address}?text=${encodeURIComponent(depositData.memo)}` : depositData.address} size={130} bgColor={"#ffffff"} fgColor={"#0f172a"} level={"H"} includeMargin={false} />
                </div>
                <div className="bg-slate-800/40 border border-white/10 p-3 rounded-2xl">
                  <span className="block text-[9px] uppercase font-bold text-slate-400 mb-0.5">Сумма к отправке</span>
                  <span className="text-base font-black text-white">{depositData.expected_amount} <span className="text-xs text-indigo-400 font-bold">USDT</span></span>
                  <span className="block text-[9px] text-slate-400 mt-0.5">Сеть: {depositData.currency}</span>
                </div>
                {depositData.memo && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/20 backdrop-blur-sm rounded-2xl">
                    <span className="block text-[9px] uppercase font-black text-amber-300 mb-1">⚠️ Обязательный комментарий (Memo)</span>
                    <div className="flex gap-1 items-center bg-slate-950/80 p-1.5 rounded-xl border border-white/10 mt-1">
                      <code className="text-xs text-amber-300 font-black tracking-wider font-mono flex-1 select-all px-1 truncate">{depositData.memo}</code>
                      <button onClick={() => { navigator.clipboard.writeText(depositData.memo); setCopiedMemo(true); setTimeout(() => setCopiedMemo(false), 2000); }} className="bg-amber-600/80 hover:bg-amber-500 text-[9px] font-bold text-white px-2 py-1 rounded-lg transition shrink-0 cursor-pointer">{copiedMemo ? '✓ Copied' : 'Copy'}</button>
                    </div>
                    <p className="text-[9px] text-amber-200/80 mt-1.5 font-medium leading-normal">Укажите этот код в поле <strong>Memo/Message</strong> при отправке. Без него средства не зачислятся!</p>
                  </div>
                )}
                <div className="bg-slate-800/40 border border-white/10 p-3 rounded-2xl">
                  <span className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Адрес назначения</span>
                  <div className="flex gap-1 items-center bg-slate-950/80 p-1.5 rounded-xl border border-white/10">
                    <code className="text-[10px] text-emerald-400 break-all font-mono flex-1 select-all overflow-hidden truncate px-1">{depositData.address}</code>
                    <button onClick={() => { navigator.clipboard.writeText(depositData.address); alert("Адрес скопирован!"); }} className="bg-slate-700/80 hover:bg-slate-600 text-[9px] font-bold text-white px-2 py-1 rounded-lg transition shrink-0 cursor-pointer">Copy</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'settings' && (
            <div className="animate-in fade-in duration-200 text-left">
              <button onClick={() => setActiveView('menu')} className="text-xs font-bold text-slate-400 hover:text-white mb-3 flex items-center gap-1 transition cursor-pointer">← Назад в меню</button>
              <h3 className="text-sm font-black text-white mb-3">⚙️ Настройки профиля</h3>
              <div className="space-y-3 text-xs text-slate-300">
                <div>
                  <label className="block text-[10px] uppercase font-bold text-slate-400 mb-1">Имя пользователя</label>
                  <input type="text" disabled value={userData.username} className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-2.5 text-slate-200 font-medium opacity-80" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase font-bold text-slate-400 mb-1">Статус профиля</label>
                  <span className="inline-block bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 font-bold px-2.5 py-1 rounded-lg">Исполнитель / Заказчик</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-normal pt-2">Для изменения персональных регистрационных данных обратитесь в поддержку.</p>
              </div>
            </div>
          )}

          {activeView === 'security' && (
            <div className="animate-in fade-in duration-200 text-left">
              <button onClick={() => setActiveView('menu')} className="text-xs font-bold text-slate-400 hover:text-white mb-3 flex items-center gap-1 transition cursor-pointer">← Назад в меню</button>
              <h3 className="text-sm font-black text-white mb-0.5">🛡️ Безопасность и ключи</h3>
              <p className="text-[10px] text-slate-400 mb-3">Настройка доступа и смены паролей.</p>
              <div className="space-y-3">
                <div className="bg-slate-800/40 border border-white/10 p-3 rounded-2xl">
                  <h4 className="text-xs font-bold text-slate-200 mb-0.5">API-ключ платформы</h4>
                  <p className="text-[9px] text-slate-400 mb-2">Используйте для интеграции и автоматизации.</p>
                  {apiKey ? (
                    <div className="space-y-2">
                      <div className="flex gap-1 items-center bg-slate-950/80 p-1.5 rounded-xl border border-white/10">
                        <code className="text-[9px] text-emerald-400 break-all select-all font-mono flex-1 overflow-x-hidden truncate px-1">{apiKey}</code>
                        <button onClick={() => { navigator.clipboard.writeText(apiKey); alert("API-ключ скопирован!"); }} className="bg-slate-700/80 hover:bg-slate-600 text-[9px] font-bold text-white px-2 py-1 rounded-lg transition cursor-pointer">Copy</button>
                      </div>
                      <button onClick={() => setApiKey('')} className="text-[9px] font-bold text-rose-400 hover:text-rose-300 transition cursor-pointer">Отозвать ключ</button>
                    </div>
                  ) : (
                    <button onClick={generateNewKey} className="w-full bg-indigo-600/80 hover:bg-indigo-500 border border-indigo-400/30 text-white text-[10px] font-bold py-2 px-3 rounded-xl transition cursor-pointer">Создать API-ключ</button>
                  )}
                </div>
                <div className="bg-slate-800/40 border border-white/10 p-3 rounded-2xl">
                  <h4 className="text-xs font-bold text-slate-200 mb-1">Изменение пароля</h4>
                  {!showPasswordForm ? (
                    <button onClick={() => setShowPasswordForm(true)} className="w-full bg-slate-700/60 hover:bg-slate-700 border border-white/10 text-slate-200 text-[10px] font-bold py-2 px-3 rounded-xl transition cursor-pointer">Сменить пароль</button>
                  ) : (
                    <div className="space-y-2 mt-2">
                      <p className="text-[9px] text-slate-400 leading-normal">Мы отправим ссылку для установки нового пароля на ваш Email.</p>
                      <button onClick={() => handleRequestPasswordReset()} disabled={isResetSending} className="w-full bg-indigo-600/80 hover:bg-indigo-500 border border-indigo-400/30 text-white text-[9px] font-bold py-2 px-2 rounded-xl transition disabled:opacity-50 cursor-pointer">{isResetSending ? '⏳ Отправка...' : '📩 Отправить ссылку на почту'}</button>
                      <button type="button" onClick={() => setShowPasswordForm(false)} className="w-full bg-slate-800 hover:bg-slate-700 text-slate-400 text-[9px] font-bold py-1.5 px-2 rounded-xl transition cursor-pointer">Отмена</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeView === 'reset_password_confirm' && (
            <div className="animate-in fade-in duration-200 text-left">
              <h3 className="text-sm font-black text-white mb-0.5">🔑 Новый пароль</h3>
              <p className="text-[10px] text-slate-400 mb-3">Придумайте новый надежный пароль.</p>
              <form onSubmit={handleConfirmPasswordReset} className="space-y-2.5">
                <div>
                  <label className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Новый пароль</label>
                  <input type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Минимум 8 символов" className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/60" />
                </div>
                <div>
                  <label className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Повторите пароль</label>
                  <input type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Повторите новый пароль" className="w-full bg-slate-950/50 border border-white/10 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/60" />
                </div>
                <button type="submit" disabled={isConfirmingPassword} className="w-full mt-2 bg-indigo-600/80 hover:bg-indigo-500 border border-indigo-400/30 text-white text-xs font-bold py-2.5 px-3 rounded-xl transition disabled:opacity-50 cursor-pointer shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
                  {isConfirmingPassword ? '⏳ Сохранение...' : 'Сохранить новый пароль'}
                </button>
              </form>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

export default UserBadge;